import React, { useEffect, useRef, useState } from 'react';
import Modal from 'src/react_components/modal';

export type AnnotationProgressBarProps = {
  done: number;
  // 0 means no done toggle to summarise, so the bar renders nothing rather than a 0% bar.
  total: number;
  // All trackable feedback is done, whichever category the done/total above are scoped to.
  isComplete: boolean;
  categoryLabel?: string;
  categoryClassName?: string;
};

const AnnotationProgressBar = ({
  done, total, isComplete, categoryLabel, categoryClassName
}: AnnotationProgressBarProps) => {
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  // Starts true when already complete on mount, so only finishing during this visit pops the modal, once.
  const hasAcknowledgedCompletionRef = useRef(isComplete);

  const percentage = total === 0 ? 0 : Math.round((done / total) * 100);

  useEffect(() => {
    if (isComplete && !hasAcknowledgedCompletionRef.current) {
      hasAcknowledgedCompletionRef.current = true;
      setShowCompletionModal(true);
    }
  }, [isComplete]);

  if (total === 0) return null;

  const label = (() => {
    if (isComplete) return "You've worked through this feedback. Nice work!";
    if (categoryLabel) return `${categoryLabel} · ${done} of ${total} addressed`;
    return `${done} of ${total} feedback addressed`;
  })();

  return (
    <div className="annotation-progress-bar">
      <div className="annotation-progress-bar-header d-flex justify-content-between align-items-center">
        <span className="annotation-progress-bar-label">
          {label}
          {isComplete && (
            <React.Fragment>
              {' '}
              <button
                type="button"
                className="btn btn-link p-0 align-baseline annotation-progress-bar-read-more"
                onClick={() => setShowCompletionModal(true)}
              >
                Read more...
              </button>
            </React.Fragment>
          )}
        </span>
        <span className="annotation-progress-bar-percentage">
          {percentage}
          %
        </span>
      </div>
      <div
        className="progress annotation-progress-bar-track"
        role="progressbar"
        aria-label={label}
        aria-valuenow={percentage}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={`progress-bar annotation-progress-bar-fill ${categoryClassName || ''}`}
          style={{ width: `${percentage}%` }}
        />
      </div>

      {isComplete && (
        <Modal
          id="annotation-progress-completion-modal"
          title="Nice work!"
          body={(
            <p>
              You&apos;ve worked through this feedback. These are examples of patterns, not a full list. Often,
              the same issues will show up elsewhere in your document, so it&apos;s worth checking the rest
              before you resubmit. Submit your revised draft when you&apos;re ready for another round of
              feedback.
            </p>
          )}
          footer={(
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setShowCompletionModal(false)}
            >
              Close
            </button>
          )}
          show={showCompletionModal}
          onClose={() => setShowCompletionModal(false)}
        />
      )}
    </div>
  );
};

export default AnnotationProgressBar;
